/**
 * Navigation primitives: Tabs (prompt.md §37, §54, §71).
 *
 * WHY the tabs are a controlled component with real `tablist`/`tab`/`tabpanel`
 * roles: the memory screen puts "Private" and "Shared" side by side and the
 * admin screen tabs between members, audit and policy — both must be operable
 * with a keyboard and announced correctly, and a shared implementation keeps
 * that true everywhere.
 */

import type { ReactNode } from "react";

import { MUTED_TEXT, rowStyle, stackStyle } from "../styles.ts";

export interface TabItem {
  readonly id: string;
  readonly label: string;
  /** Optional trailing detail such as a count; also announced to screen readers. */
  readonly badge?: ReactNode;
}

export interface TabsProps {
  readonly items: readonly TabItem[];
  readonly activeId: string;
  readonly onChange: (id: string) => void;
  readonly label: string;
  readonly children: ReactNode;
}

export function Tabs({ items, activeId, onChange, label, children }: TabsProps) {
  return (
    <div style={stackStyle("var(--dayvise-space-md)")}>
      <div role="tablist" aria-label={label} style={{ ...rowStyle("var(--dayvise-space-xxs)"), borderBottom: "1px solid var(--dayvise-color-border)" }}>
        {items.map((item) => {
          const selected = item.id === activeId;
          return (
            <button
              key={item.id}
              type="button"
              role="tab"
              id={`tab-${item.id}`}
              aria-selected={selected}
              aria-controls={`panel-${item.id}`}
              tabIndex={selected ? 0 : -1}
              onClick={() => onChange(item.id)}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "var(--dayvise-space-xs)",
                padding: "var(--dayvise-space-sm) var(--dayvise-space-md)",
                background: "transparent",
                border: "none",
                borderBottom: `2px solid ${selected ? "var(--dayvise-color-accent)" : "transparent"}`,
                color: selected ? "var(--dayvise-color-text-primary)" : "var(--dayvise-color-text-muted)",
                fontFamily: "var(--dayvise-font-family-sans)",
                fontSize: "var(--dayvise-font-size-sm)",
                fontWeight: selected ? "var(--dayvise-font-weight-semibold)" : "var(--dayvise-font-weight-regular)",
                cursor: "pointer",
              }}
            >
              {item.label}
              {item.badge === undefined ? null : <span style={MUTED_TEXT}>{item.badge}</span>}
            </button>
          );
        })}
      </div>
      <div role="tabpanel" id={`panel-${activeId}`} aria-labelledby={`tab-${activeId}`}>
        {children}
      </div>
    </div>
  );
}
